import { getAuthToken } from '../../services/auth/tokenStorage';
import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Alert, ActivityIndicator, Image, SafeAreaView, KeyboardAvoidingView, Platform, ScrollView } from 'react-native';
import { NavigationProp } from '@react-navigation/native';
import { API_URL } from '../../config';
import OrnamentBackground from '../../components/OrnamentBackground';
import VerificationCodeInput from '../../components/VerificationCodeInput';
import backIcon from '../../assets/icons/back.png';

import { createLogger } from '../../utils/logger';
import { ms, s, vs } from '../../utils/responsive';
import { wipeAllUserData } from '../../services/auth/logoutWipe';
const logger = createLogger('DeleteAccount');

interface DeleteAccountScreenProps {
  navigation: NavigationProp<any>;
}

const DeleteAccountScreen: React.FC<DeleteAccountScreenProps> = ({ navigation }) => {
  const [verificationText, setVerificationText] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    // Show warning when user starts typing
    if (verificationText.length > 0 && !showWarning) {
      setShowWarning(true);
    }
  }, [verificationText]);

  const handleDelete = async () => {
    if (verificationText !== 'DELETE') {
      return;
    }

    // Show confirmation alert
    Alert.alert(
      'Confirm Account Deletion',
      'Are you absolutely sure? This will permanently delete your ENTIRE account including:\n\n• Your account credentials (email, password)\n• Your name and profile\n• Personal Information\n• Medical Records\n• Financial Data\n• Documents\n• Passwords\n• All shared data with successors/keyholders\n\nYou will be logged out and will need to create a new account to use MyFinalKey again.\n\nThis action CANNOT be undone.',
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: 'Delete Account',
          style: 'destructive',
          onPress: async () => {
            try {
              setIsDeleting(true);
              const token = await getAuthToken();

              if (!token) {
                Alert.alert('Error', 'Authentication required. Please log in again.');
                setIsDeleting(false);
                return;
              }

              // Call backend API to delete account
              logger.debug('Calling delete account endpoint:', `${API_URL}/users/delete-account`);
              const response = await fetch(`${API_URL}/users/delete-account`, {
                method: 'DELETE',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              });

              logger.debug('Delete account response status:', response.status);
              const result = await response.json().catch(() => ({}));

              if (response.ok && result.success) {
                // Full wipe: tokens, keychain entries (salt, biometric cred,
                // master key), session AsyncStorage, profile-image caches,
                // in-memory caches, error-log buffer.
                await wipeAllUserData();

                // Navigate to Auth flow (which starts at Splash screen) and reset navigation stack
                navigation.reset({
                  index: 0,
                  routes: [{ name: 'Auth' }],
                });

                // Show success message after navigation
                setTimeout(() => {
                  Alert.alert(
                    'Account Deleted',
                    'Your account has been permanently deleted. We\'re sorry to see you go.'
                  );
                }, 500);
              } else {
                Alert.alert(
                  'Deletion Failed',
                  result.message || 'Failed to delete account. Please try again or contact support.'
                );
              }
            } catch (error) {
              logger.error('Error deleting account:', error);
              Alert.alert(
                'Error',
                'An error occurred while deleting your account. Please try again or contact support.'
              );
            } finally {
              setIsDeleting(false);
            }
          },
        },
      ]
    );
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
    >
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <SafeAreaView style={styles.headerContainer}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Image source={backIcon} style={styles.backIcon} />
          </TouchableOpacity>
        </SafeAreaView>
        <OrnamentBackground />

        <Text style={styles.title}>Delete Account</Text>
        <Text style={styles.description}>
          This action cannot be undone. All your data will be permanently deleted.
        </Text>

        {showWarning && (
          <View style={styles.warningContainer}>
            <Text style={styles.warningTitle}>Warning: Account Deletion</Text>
            <Text style={styles.warningMessage}>
              You are about to delete your account permanently. This action cannot be reversed.
              Please type "DELETE" to confirm. You will be logged out immediately.
            </Text>
          </View>
        )}

        <View style={styles.lightGreyBox}>
          <View style={styles.inputContainer}>
            <Text style={styles.inputLabel}>Type "DELETE" to confirm</Text>
            <VerificationCodeInput
              value={verificationText}
              onChangeText={setVerificationText}
              length={6}
              cellSpacing={10}
              isNumeric={false}
            />

            <TouchableOpacity
              style={[styles.deleteButton, { opacity: verificationText === 'DELETE' && !isDeleting ? 1 : 0.6 }]}
              onPress={handleDelete}
              disabled={verificationText !== 'DELETE' || isDeleting}
            >
              {isDeleting ? (
                <ActivityIndicator color="#FFFFFF" size="small" />
              ) : (
                <Text style={styles.deleteButtonText}>Delete Account</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingBottom: ms(40),
  },
  headerContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
  backButton: {
    padding: ms(20),
    paddingTop: ms(10),
  },
  backIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#FFFFFF',
  },
  lightGreyBox: {
    width: '100%',
    backgroundColor: '#EFF2F5',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: ms(20),
    paddingVertical: ms(40),
    marginTop: 'auto',
  },
  inputContainer: {
    width: '100%',
    alignItems: 'center',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(10),
    marginTop: ms(100),
  },
  description: {
    fontSize: ms(18, 0.3),
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: ms(30),
    paddingHorizontal: ms(20),
  },
  warningContainer: {
    width: "90%",
    backgroundColor: "#E85C5C",
    borderRadius: ms(30),
    padding: ms(20),
    marginBottom: ms(20),
    alignSelf: "center",
  },
  warningTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    textAlign: "center",
  },
  warningMessage: {
    fontSize: ms(16, 0.3),
    color: '#FFFFFF',
    paddingHorizontal: ms(10),
    marginTop: ms(10),
    textAlign: "center",
    lineHeight: ms(22, 0.3),
  },
  inputLabel: {
    fontSize: ms(18, 0.3),
    color: '#A9A9A9',
    alignSelf: 'flex-start',
    marginBottom: ms(20),
  },
  deleteButton: {
    backgroundColor: '#E85C5C',
    width: '80%',
    height: s(50),
    borderRadius: ms(25),
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: ms(30),
  },
  deleteButtonText: {
    color: '#FFFFFF',
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
  },
});

export default DeleteAccountScreen;
